"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { isAnalyticsAllowed } from "@/lib/cookies";
import type { LifecycleStage } from "@/types/intercom";

/**
 * Compute lifecycle stage based on workspace data.
 * Simple heuristic — can be refined with actual product usage data.
 */
function computeLifecycleStage(
  onboardingCompleted: boolean,
  plan: string,
  lastSignIn: string | null,
): LifecycleStage {
  if (!onboardingCompleted) return "signup";

  // Check for churning: no sign-in in 14+ days
  if (lastSignIn) {
    const daysSinceSignIn =
      (Date.now() - new Date(lastSignIn).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceSignIn > 14) return "churning";
  }

  if (plan === "enterprise" || plan === "business") return "power_user";
  if (plan === "pro") return "engaged";
  return "activated";
}

function IntercomInit() {
  const bootedRef = useRef(false);
  const { user } = useAuth();

  useEffect(() => {
    function tryBoot() {
      // Intercom sends PII (email, name) — require analytics consent (LGPD Art. 7)
      if (!isAnalyticsAllowed()) return;

      const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;
      if (!appId) return;
      if (bootedRef.current) return;

      async function bootIntercom() {
        if (!user) {
          window.Intercom?.("boot", { app_id: appId });
          return;
        }

        // Fetch workspace data and identity hash via API
        let workspaceData: Record<string, unknown> | null = null;
        let role = "viewer";
        let userHash: string | undefined;

        try {
          const res = await fetch("/api/intercom/setup");
          if (res.ok) {
            const data = await res.json();
            workspaceData = data.workspace || null;
            role = data.role || "viewer";
            userHash = data.user_hash;
          }
        } catch {
          // Continue without workspace data
        }

        const lifecycleStage = computeLifecycleStage(
          (workspaceData?.onboarding_completed as boolean) ?? false,
          (workspaceData?.plan as string) ?? "standard",
          null,
        );

        const bootPayload: Record<string, unknown> = {
          app_id: appId,
          user_id: user.sub,
          email: user.email,
          name: user.name || user.email,
          plan: (workspaceData?.plan as string) || "standard",
          workspace_name: (workspaceData?.name as string) || "",
          workspace_slug: (workspaceData?.slug as string) || "",
          role,
          lifecycle_stage: lifecycleStage,
        };

        if (userHash) {
          bootPayload.user_hash = userHash;
        }

        if (workspaceData?.industry) {
          bootPayload.industry = workspaceData.industry;
        }
        if (workspaceData?.company_size) {
          bootPayload.company_size = workspaceData.company_size;
        }

        // Company (workspace) identification
        if (workspaceData?.id) {
          bootPayload.company = {
            company_id: workspaceData.id,
            name: workspaceData.name,
            plan: workspaceData.plan,
            industry: workspaceData.industry || undefined,
            size: workspaceData.company_size || undefined,
            created_at: workspaceData.created_at
              ? Math.floor(
                  new Date(workspaceData.created_at as string).getTime() / 1000,
                )
              : undefined,
          };
        }

        window.Intercom?.("boot", bootPayload);
      }

      // Load the Intercom Messenger script
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${appId}`;
      script.onload = () => {
        bootIntercom();
      };
      document.head.appendChild(script);

      // Initialize Intercom stub before script loads
      if (!window.Intercom) {
        const q: unknown[][] = [];
        const stub = (_action: string, ...args: unknown[]) => {
          q.push([_action, ...args]);
        };
        (stub as unknown as { q: unknown[][] }).q = q;
        window.Intercom = stub;
      }

      bootedRef.current = true;
    }

    // Try on mount (consent may already exist)
    tryBoot();

    // Listen for consent changes (grant or revocation)
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && !detail.analytics && bootedRef.current) {
        // Analytics consent revoked mid-session — shutdown Intercom (LGPD Art. 8 §5)
        window.Intercom?.("shutdown");
        bootedRef.current = false;
      } else {
        tryBoot();
      }
    };
    window.addEventListener("cookie-consent-change", handler);

    return () => {
      window.removeEventListener("cookie-consent-change", handler);
      if (bootedRef.current) {
        window.Intercom?.("shutdown");
      }
    };
  }, [user]);

  return null;
}

export function IntercomProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <IntercomInit />
      {children}
    </>
  );
}
